import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Container, GalleryBackButton, GalleryContainer, Section, SectionHeader, SubSection, LeftGalleryArrow, RightGalleryArrow } from '../styles';
import { Header } from './header';
import { useGallery } from '../shared/contexts/GalleryContext';
import { GalleryLoadingSpinner } from '../components/LoadingSpinner';
import { FullSizeImage, ThumbnailImage } from '../components/GalleryImage';
import { AnimatedFullSizeImage, AnimatedThumbnailImage } from '../components/AnimatedGalleryImage';
import { EnhancedGalleryNavigation } from '../components/GalleryNavigation';
import { transformImageUrlForEnvironment } from '../utils/imageUrlUtils';
import styled from 'styled-components';

import arrow from '../images/arrow.png';
import x from '../images/x.png';
import { Link, useParams } from 'react-router-dom';

// Styled component for gallery links to ensure proper layout
const GalleryLink = styled(Link)`
    display: inline-block;
    text-decoration: none;
    width: calc(33.333% - 3px);
    flex: 0 0 calc(33.333% - 3px);
`;

// Grid layout component (adjusted to match beta spacing)
const Grid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    max-width: 1060px;
    margin: 0 auto;
    padding: 20px;
    justify-content: center;
`;

// Markdown content styling for gallery descriptions
const MarkdownContent = styled.div`
    & p {
        margin-bottom: 1em;
        line-height: 1.6;
    }
    
    & h1, & h2, & h3, & h4, & h5, & h6 {
        margin-top: 1.5em;
        margin-bottom: 0.5em;
        font-weight: 600;
    }
    
    & h1 { font-size: 2em; }
    & h2 { font-size: 1.5em; }
    & h3 { font-size: 1.25em; }
    
    & ul, & ol {
        margin-left: 1.5em;
        margin-bottom: 1em;
    }
    
    & li {
        margin-bottom: 0.5em;
    }
    
    & strong {
        font-weight: 600;
    }
    
    & em {
        font-style: italic;
    }
    
    & a {
        color: #007bff;
        text-decoration: none;
        
        &:hover {
            text-decoration: underline;
        }
    }
    
    & code {
        background-color: #f4f4f4;
        padding: 2px 6px;
        border-radius: 3px;
        font-family: 'Courier New', monospace;
        font-size: 0.9em;
    }
    
    & pre {
        background-color: #f4f4f4;
        padding: 1em;
        border-radius: 6px;
        overflow-x: auto;
        margin-bottom: 1em;
        
        & code {
            background-color: transparent;
            padding: 0;
        }
    }
    
    & blockquote {
        border-left: 4px solid #ddd;
        padding-left: 1em;
        margin-left: 0;
        color: #666;
        font-style: italic;
    }
`;

export const Gallery = () => {
    const { 
        galleryItems, 
        loading: galleryLoading, 
        navigationLoading: contextNavigationLoading,
        setNavigationLoading: setContextNavigationLoading,
        preloadImage 
    } = useGallery();
    const [selected, setSelected] = useState<number | null>(null);
    const [localNavigationLoading, setLocalNavigationLoading] = useState(false);

    // Show loading state while gallery items are loading
    if (galleryLoading) {
        return (
            <Container>
                <Header />
                <Section>
                    <SectionHeader>Gallery</SectionHeader>
                    <GalleryContainer>
                        <GalleryLoadingSpinner text="Loading gallery items..." />
                    </GalleryContainer>
                </Section>
            </Container>
        );
    }

    // Enhanced navigation with loading states
    const handleNavigate = async (newIndex: number) => {
        if (newIndex < 0 || newIndex >= galleryItems.length) return;
        
        setLocalNavigationLoading(true);
        setContextNavigationLoading(true);
        
        try {
            // Preload the next image
            const nextItem = galleryItems[newIndex];
            if (nextItem?.full_image_url) {
                await preloadImage(transformImageUrlForEnvironment(nextItem.full_image_url));
            }
        } catch (error) {
            console.warn('Failed to preload image:', error);
        } finally {
            setLocalNavigationLoading(false);
            setContextNavigationLoading(false);
        }
    };

    // Show selected item view
    if (selected != null && selected >= 0) {
        const item = galleryItems[selected];
        const isLoading = localNavigationLoading || contextNavigationLoading;
        
        return (
            <Container>
                <Header />
                <Section>
                    <SectionHeader>Gallery</SectionHeader>
                    <GalleryContainer>
                        <Section>
                            <SubSection>
                                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                    <GalleryBackButton 
                                        src={x} 
                                        onClick={() => setSelected(null)} />
                                </div>
                                <div style={{ position: 'relative', paddingLeft: '40px', paddingRight: '40px' }}>
                                    <FullSizeImage 
                                        src={transformImageUrlForEnvironment(item.full_image_url || item.thumbnail_url)}
                                        alt={item.title}
                                        onLoad={() => setLocalNavigationLoading(false)}
                                    />
                                </div>
                                <EnhancedGalleryNavigation
                                    currentIndex={selected}
                                    totalItems={galleryItems.length}
                                    onPrevious={() => setSelected(selected - 1)}
                                    onNext={() => setSelected(selected + 1)}
                                    isLoading={isLoading}
                                    items={galleryItems}
                                    onNavigate={handleNavigate}
                                    preloadNext={true}
                                />
                                <h2>{item.title}</h2>
                                {item.description && (
                                    <MarkdownContent>
                                        <ReactMarkdown 
                                            remarkPlugins={[remarkGfm]}
                                            components={{
                                                a: ({ ...props }) => (
                                                    <a {...props} target="_blank" rel="noopener noreferrer" />
                                                ),
                                            }}
                                        >
                                            {item.description}
                                        </ReactMarkdown>
                                    </MarkdownContent>
                                )}
                            </SubSection>
                        </Section>
                    </GalleryContainer>
                </Section>
            </Container>
        );
    }

    // Show gallery grid view
    if (galleryItems.length === 0) {
        return (
            <Container>
                <Header/>
                <Section>
                    <SectionHeader>Gallery</SectionHeader>
                    <GalleryContainer>
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                            No gallery items available.
                        </div>
                    </GalleryContainer>
                </Section>
            </Container>
        );
    }

    return (
        <Container>
            <Header/>
            <Section>
                <SectionHeader>Gallery</SectionHeader>
                <GalleryContainer>
                    <Section>
                        <SubSection>
                        {
                            galleryItems.map((item, index) => (
                                <ThumbnailImage 
                                    key={item.id} 
                                    src={transformImageUrlForEnvironment(item.thumbnail_url)} 
                                    onClick={() => {
                                        setSelected(index);
                                        // Preload the full image when clicking thumbnail
                                        if (item.full_image_url) {
                                            preloadImage(transformImageUrlForEnvironment(item.full_image_url));
                                        }
                                    }}
                                    alt={item.title}
                                    fetchPriority={index === 0 ? 'high' : 'auto'} />
                            ))
                        }
                        </SubSection>
                    </Section>
                </GalleryContainer>
            </Section>
        </Container>
    );
}

export const GalleryItem = () => {
    const { index } = useParams();
    const { galleryItems, loading: galleryLoading } = useGallery();

    let itemIndex = 0;
    if (index) {
        itemIndex = Number(index);
    }

    // Show loading state while gallery items are loading
    if (galleryLoading) {
        return (
            <Container>
                <Header />
                <Section>
                    <SectionHeader>Gallery</SectionHeader>
                    <GalleryLoadingSpinner text="Loading gallery items..." />
                </Section>
            </Container>
        );
    }

    // Check if index is valid for gallery
    if (itemIndex < 0 || itemIndex >= galleryItems.length) {
        return (
            <Container>
                <Header />
                <Section>
                    <SectionHeader>Gallery</SectionHeader>
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        Gallery item not found.
                    </div>
                </Section>
            </Container>
        );
    }

    const item = galleryItems[itemIndex];
    return (
        <Container>
            <Header />
            <Section>
                <GalleryContainer>
                    <Section>
                        <SubSection>
                            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <Link to='/'>
                                    <GalleryBackButton src={x} />
                                </Link>
                            </div>
                            <div style={{ position: 'relative', paddingLeft: '40px', paddingRight: '40px' }}>
                                <AnimatedFullSizeImage src={transformImageUrlForEnvironment(item.full_image_url)} alt={item.title} />
                            </div>
                            <p>
                                {itemIndex > 0 &&
                                    <Link to={`/gallery/${itemIndex - 1}`}>
                                        <LeftGalleryArrow src={arrow}/>
                                    </Link>
                                }
                                {itemIndex < galleryItems.length - 1 &&
                                    <Link to={`/gallery/${itemIndex + 1}`}>
                                        <RightGalleryArrow src={arrow}/>
                                    </Link>
                                }
                            </p>
                            <h2>
                                {item.title}
                            </h2>
                            {item.description && (
                                <MarkdownContent>
                                    <ReactMarkdown 
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                            a: ({ ...props }) => (
                                                <a {...props} target="_blank" rel="noopener noreferrer" />
                                            ),
                                        }}
                                    >
                                        {item.description}
                                    </ReactMarkdown>
                                </MarkdownContent>
                            )}
                        </SubSection>
                    </Section>
                </GalleryContainer>
            </Section>
        </Container>
    );
}

export const GalleryComponent = () => {
    const { galleryItems, loading: galleryLoading } = useGallery();

    // Show loading state while gallery items are loading
    if (galleryLoading) {
        return (
            <Section>
                <GalleryContainer>
                    <GalleryLoadingSpinner text="Loading gallery items..." />
                </GalleryContainer>
            </Section>
        );
    }

    // Show gallery grid view
    if (galleryItems.length === 0) {
        return (
            <Section>
                <GalleryContainer>
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        No gallery items available.
                    </div>
                </GalleryContainer>
            </Section>
        );
    }

    return (
        <Section>
            <GalleryContainer>
                <Section>
                    <SubSection>
                        <SectionHeader>Gallery</SectionHeader>
                        <Grid>
                            {
                                galleryItems.map((item, index) => (
                                    <GalleryLink key={item.id} to={`/gallery/${index}`}>
                                        <AnimatedThumbnailImage
                                            src={transformImageUrlForEnvironment(item.thumbnail_url)}
                                            alt={item.title}
                                            fetchPriority={index === 0 ? 'high' : 'auto'} />
                                    </GalleryLink>
                                ))
                            }
                        </Grid>
                    </SubSection>
                </Section>
            </GalleryContainer>
        </Section>
    );
}